import type { ICellRendererParams } from 'ag-grid-community'
import { createTemplateRenderer } from '../utils/fast-renderer'
import { readArrayPath } from '../utils/object-path'
import type { StepperValueItem } from '../utils/transforms'
import { isValidStepperData } from '../utils/transforms'

/** Extended cell renderer params with editor params from column config */
type CellRendererParamsWithEditorParams = ICellRendererParams & {
  readonly editorParams?: {
    readonly values?: readonly StepperValueItem[]
  }
}

/** Normalised stepper render input — a value (1-indexed) and its step options */
type NormalizedStepper = {
  readonly value: number
  readonly label: string
  readonly options: readonly StepperValueItem[]
}

/**
 * Extract stepper options from either:
 * 1. Column config (cellRendererParams.editorParams) - set at column definition time
 * 2. Cell data (cellData.editorParams) - set per-cell from backend
 */
const getStepperOptions = (
  params: ICellRendererParams,
  cellData: unknown
): readonly StepperValueItem[] => {
  const columnEditorParams = (params as CellRendererParamsWithEditorParams)
    .editorParams

  if (columnEditorParams?.values) {
    return columnEditorParams.values
  }

  return readArrayPath<StepperValueItem>(cellData, ['editorParams', 'values'])
}

/**
 * Backend always sends: { value: 1-indexed number, label, editorParams: { values } }
 * Where value=1 means first option, value=2 means second option, etc.
 */
const normalizeStepperData = (
  data: unknown,
  params: ICellRendererParams
): NormalizedStepper => {
  const options = getStepperOptions(params, data)

  if (isValidStepperData(data)) {
    return {
      value: typeof data.value === 'number' ? data.value : 0,
      label: data.label || '',
      options
    }
  }

  return { value: 0, label: '', options }
}

/** SVG path for the first step (flag/pennant shape). */
const FLAG_PATH =
  'M246.66,123.56,201,55.13A15.94,15.94,0,0,0,187.72,48H40A16,16,0,0,0,24,64V192a16,16,0,0,0,16,16H187.72A16,16,0,0,0,201,200.88h0l45.63-68.44A8,8,0,0,0,246.66,123.56Z'

/** SVG path for subsequent steps (chevron/arrow shape). */
const CHEVRON_PATH =
  'M237.3,136.88l-42.66,64A16,16,0,0,1,181.33,208H24a8,8,0,0,1-6.58-12.55L64,128,17.42,60.55A8,8,0,0,1,24,48H181.33a16,16,0,0,1,13.31,7.12l42.66,64A16,16,0,0,1,237.3,136.88Z'

/**
 * Container skeleton — parsed ONCE, cloned per cell. Step children are
 * reconciled into it in `updateStepper` (a variable number per row).
 */
const STEPPER_TEMPLATE =
  '<span role="img" style="display:inline-flex;align-items:center;"></span>'

/**
 * One-step skeleton — parsed ONCE, cloned per step. The path `d`, color, and
 * active state are written per step in `applyStep`.
 */
const STEP_TEMPLATE =
  '<span data-step style="margin-left:-6px;display:inline-flex;width:29px;height:28px">' +
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" width="29" height="28">' +
  '<path></path></svg></span>'

const stepFactory = document.createElement('template')
stepFactory.innerHTML = STEP_TEMPLATE

const stepPrototype = stepFactory.content.firstElementChild! as HTMLElement

/** Clone a fresh step node from the pre-parsed step skeleton */
const cloneStep = (): HTMLElement =>
  stepPrototype.cloneNode(true) as HTMLElement

/**
 * Write one step's data onto a (possibly reused) step node in place. Active
 * steps use the colour's -10 shade; inactive steps the muted -4 shade.
 */
const applyStep = (
  node: HTMLElement,
  step: StepperValueItem,
  index: number,
  isActive: boolean
): void => {
  const color = step.color ?? 'gray'
  const colorVar = isActive
    ? `var(--color-${color}-10)`
    : `var(--color-${color}-4)`

  node.setAttribute('data-step', String(index + 1))
  node.setAttribute('data-active', String(isActive))
  node.title = step.title ?? step.label ?? ''
  node.style.color = colorVar

  const path = node.querySelector('path')!
  path.setAttribute('d', index === 0 ? FLAG_PATH : CHEVRON_PATH)
}

/**
 * Reconcile the container's step children to match `options` — append cloned
 * step nodes when more are needed, remove extras when fewer. DOM-child diffing,
 * never innerHTML, so the hot scroll path never re-parses.
 */
const reconcileSteps = (
  container: HTMLElement,
  normalized: NormalizedStepper
): void => {
  const { options, value } = normalized

  while (container.childElementCount < options.length) {
    container.appendChild(cloneStep())
  }

  while (container.childElementCount > options.length) {
    container.removeChild(container.lastElementChild!)
  }

  options.forEach((step, index) => {
    const node = container.children[index] as HTMLElement
    applyStep(node, step, index, value > index)
  })
}

/**
 * Apply stepper data to a cloned container in place — no re-parse, no re-clone.
 * Empty options clear all step children and render a blank cell.
 */
const updateStepper = (
  container: HTMLElement,
  data: unknown,
  params: ICellRendererParams
): void => {
  const normalized = normalizeStepperData(data, params)

  container.setAttribute('aria-label', normalized.label)

  reconcileSteps(container, normalized)
}

/**
 * Stepper cell renderer — clones a pre-parsed container and reconciles its step
 * children on refresh (no innerHTML re-parse on the hot scroll path).
 *
 * Read-only visual; no click interaction in grid cells. A static SVG renderer
 * keeps dense tables fast by avoiding a custom element per cell.
 */
export const StepperComponent = createTemplateRenderer<unknown>(
  STEPPER_TEMPLATE,
  updateStepper
)
